/**
 * Place search input using Google Places.
 * Used when REACT_APP_GOOGLE_MAPS_API_KEY is set and the script is loaded.
 *
 * Renders its OWN in-flow suggestions dropdown (via AutocompleteService +
 * PlacesService) rather than Google's body-appended `.pac-container` widget.
 * The widget positions itself relative to the document and breaks inside
 * position:fixed / portaled modals (e.g. the clip lightbox); an in-flow
 * dropdown lives in the React tree and works everywhere. Normalizes to
 * { label, display_name, lat, lon, ... } for consistency with Nominatim.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMaps } from '../GoogleMapsProvider';
import { useAuth } from '../../auth/AuthContext';
import { reportUsage } from '../../services/usageBeacon';

function normalizePlace(place) {
  if (!place) return null;
  const loc = place.geometry?.location;
  const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
  const lon = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
  const label = place.name || place.formatted_address || '';
  const display_name = place.formatted_address || place.name || label;
  const subtitle = place.name && place.formatted_address ? place.formatted_address : null;
  return {
    label,
    display_name,
    subtitle,
    lat: lat != null ? Number(lat) : null,
    lon: lon != null ? Number(lon) : null,
    place_id: place.place_id,
    formatted_address: place.formatted_address,
  };
}

export default function GooglePlacesAutocompleteInput({
  value,
  onChange,
  onSelect,
  placeholder,
  bounds,
}) {
  const { isLoaded } = useGoogleMaps();
  const { apiPaused } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const serviceRef = useRef(null);
  const detailsRef = useRef(null);
  const hasUserTypedRef = useRef(false);
  const suppressNextFetchRef = useRef(false);

  // Lazily create the (singleton) Google services once the API is loaded.
  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    if (!serviceRef.current) serviceRef.current = new window.google.maps.places.AutocompleteService();
    if (!detailsRef.current) {
      detailsRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, [isLoaded]);

  // Debounced predictions fetch as the user types.
  useEffect(() => {
    if (!isFocused || !serviceRef.current || apiPaused) return;
    if (suppressNextFetchRef.current) { suppressNextFetchRef.current = false; return; }
    if (!hasUserTypedRef.current) return;

    const q = String(value || '').trim();
    if (q.length < 2) { setSuggestions([]); setActiveIdx(-1); return; }

    const t = setTimeout(() => {
      reportUsage('google_maps', 'places_autocomplete');  // billable per request
      serviceRef.current.getPlacePredictions(
        { input: q, ...(bounds && { bounds }) },
        (predictions, status) => {
          const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
          if (status !== ok || !Array.isArray(predictions)) { setSuggestions([]); setActiveIdx(-1); return; }
          setSuggestions(predictions.map((p) => ({
            place_id: p.place_id,
            label: p.structured_formatting?.main_text || p.description,
            subtitle: p.structured_formatting?.secondary_text || null,
          })));
          setActiveIdx(-1);
        }
      );
    }, 250);
    return () => clearTimeout(t);
  }, [value, isFocused, bounds, apiPaused]);

  const choose = (s) => {
    if (!s?.place_id || !detailsRef.current || apiPaused) return;
    suppressNextFetchRef.current = true;
    hasUserTypedRef.current = false;
    setSuggestions([]);
    setActiveIdx(-1);
    reportUsage('google_maps', 'places_details');  // billable per selection
    detailsRef.current.getDetails(
      { placeId: s.place_id, fields: ['place_id', 'geometry', 'name', 'formatted_address'] },
      (place, status) => {
        const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        const normalized = status === ok && place ? normalizePlace(place) : { label: s.label, display_name: s.label };
        if (normalized) onSelect?.(normalized);
      }
    );
  };

  return (
    <div>
      <input
        type="text"
        placeholder={placeholder}
        value={value || ''}
        autoComplete="off"
        onChange={(e) => { hasUserTypedRef.current = true; onChange(e.target.value); }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          hasUserTypedRef.current = false;
          setTimeout(() => { setSuggestions([]); setActiveIdx(-1); }, 150);
        }}
        onKeyDown={(e) => {
          if (!suggestions.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); choose(suggestions[activeIdx >= 0 ? activeIdx : 0]); }
          else if (e.key === 'Escape') { setSuggestions([]); setActiveIdx(-1); }
        }}
      />

      {isFocused && suggestions.length > 0 && (
        <div
          className="tp-card tp-card--tight"
          style={{ marginTop: 10 }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((s, idx) => (
            <div
              key={s.place_id || idx}
              onMouseDown={() => choose(s)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                background: idx === activeIdx ? 'var(--tp-surface-2)' : 'transparent',
              }}
            >
              <div style={{ fontSize: 14 }}>{s.label}</div>
              {!!s.subtitle && (
                <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
